import React, { useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"

import { OMReference } from "@apptivity-lab/firmament-node-sdk"

import { CategorizableCategoryTagsView } from "firmament-packages/categories"
import "@firmament-packages/categories/extensions/CategorizableExtensions"
import { CnxMeeting } from "firmament-packages/connectx"
import "@firmament-packages/connectx/extensions/CnxMeetingExtensions"

import { AffExpertProfile } from "@firmament-packages/aff"

import TextInfoView from "@components/TextInfoView"
import { Segment, SegmentedControl } from "@components/SegmentedControl"
import { AffExpertProfileAboutView } from "../AffExpertProfile"
import { ConcreteCategorizable } from "@firmament-packages"

const CnxMeetingDetailView: React.FunctionComponent<{
    meeting: CnxMeeting
}> = ({ meeting }) => {
    const coverImageUrl = meeting.coverPhoto?.actualObject?.preview || meeting.coverPhoto?.actualObject?.url ||
        meeting.meetingTemplate?.actualObject?.coverPhoto?.actualObject?.preview || meeting.meetingTemplate?.actualObject?.coverPhoto?.actualObject?.url

    const concreteCategorizable = useMemo(() => {
        return new OMReference(ConcreteCategorizable, meeting.id).actualObject
    }, [meeting])

    const expertProfile = useMemo(() => {
        if (!meeting.provider) {
            return undefined
        }

        return new OMReference(AffExpertProfile, meeting.provider.id).actualObject
    }, [meeting.provider])

    const [detailSegment, setDetailSegment] = useState("SUMMARY")

    return (
        <div id="meetingDetailView">
            <div className="relative aspect-video">
                {coverImageUrl && <Image src={coverImageUrl} alt={meeting.title} layout="fill" objectFit="cover" />}
            </div>

            <div className="py-[16px] sm:py-[24px] px-[20px] bg-white-900">
                <h1 className="text-title3 text-left text-black-900">{meeting.title}</h1>

                {expertProfile &&
                    <div className="mt-[16px]">
                        <div className="flex items-center gap-x-[12px]">
                            <div className="relative w-[44px] h-[44px] overflow-hidden bg-white-600 rounded-full">
                                <Image layout="fill" objectFit="cover"
                                    src={expertProfile.photo?.actualObject?.url || "/images/ic_avatar.svg"}
                                    alt={`${expertProfile.name || "Expert"} Profile Photo`} />
                            </div>

                            <div>
                                <div className="text-caption1 text-black-500 uppercase">AFF Expert</div>
                                <div className="text-body text-black-900">{expertProfile.name}</div>
                            </div>
                        </div>
                    </div>
                }

                <div className="flex mt-[24px] sm:gap-x-[36px]">
                    <TextInfoView className="flex-1 sm:flex-none"
                        title="Type"
                        value={concreteCategorizable?.meetingTypeCategory()?.title}
                        iconSrc={concreteCategorizable?.meetingTypeCategory()?.defaultIconSrc("tertiary")}
                        />

                    <TextInfoView className="flex-1 sm:flex-none"
                        title="Duration"
                        value={`${meeting.scheduledDurationSeconds() / 60} mins`}
                        iconSrc={"/images/ic_info_duration_tertiary.svg"}
                        />

                    <TextInfoView className="flex-1 sm:flex-none"
                        title="Rating"
                        value={meeting.overallClassRatingAverage().toFixed(1)}
                        iconSrc={"/images/ic_info_rating_tertiary.svg"}
                        />
                </div>

                <hr className="border-black-100 my-[24px]" />

                <SegmentedControl value={detailSegment} onChange={(value) => setDetailSegment(value as string)}>
                    <Segment title="Details" value="SUMMARY" key="SUMMARY" />
                    <Segment title="AFF Expert" value="PROVIDER" key="PROVIDER" />
                </SegmentedControl>

                {detailSegment === "SUMMARY" &&
                    <div className="mt-[24px]">
                        <div className="grid grid-cols-12 gap-x-[8px] gap-y-[24px]">
                            <TextInfoView className="col-span-12 sm:col-span-4"
                                title="Intensity"
                                value={concreteCategorizable?.intensityCategory()?.title || "-"}
                            />

                            <TextInfoView className="col-span-12 sm:col-span-8"
                                title="Accessibility"
                                value={concreteCategorizable?.accessibilityCategories().map((each) => each.title || "Untitled").sort().join(", ") || "-"}
                            />
                        </div>

                        <p className="mt-[24px] text-body text-left text-black-900 whitespace-pre-line">{meeting.summary || meeting.meetingTemplate?.actualObject?.summary || "-"}</p>

                        <CategorizableCategoryTagsView className="mt-[16px]"
                            subject={meeting}
                            restrictToCategoryTypes={["SUBJECT"]} />
                    </div>
                }

                {detailSegment === "PROVIDER" && expertProfile &&
                    <AffExpertProfileAboutView className="mt-[24px]" expertProfile={expertProfile} />
                }

                <hr className="border-black-100 my-[24px]" />

                <div className="flex items-center gap-x-[8px]">
                    <Image width="20" height="20" src="/images/ic_share_primary.svg" alt="Share Icon" />
                    <Link href={`mailto:?subject=${meeting.title}`}>
                        <a className="text-callout text-primary-900 underline">Email to Friend</a>
                    </Link>
                </div>
            </div>
        </div>
    )
}

export default CnxMeetingDetailView
